import { isDefined, toError } from "@clipboard-health/util-ts";
import { Box, Link } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { SystemInlineBanner, Text } from "@src/appV2/redesign/system/ui-components";
import { SupportArticles, SupportContext } from "@src/appV2/support/constants";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { type Worker } from "@src/appV2/Worker/api/types";

interface JobSearchAgreementBannersProps {
  showAgreementScrolledErrorBanner: boolean;
  showConfirmationErrorBanner: boolean;
  worker: Worker;
  onDismiss?: () => void;
}

export function JobSearchAgreementBanners(props: JobSearchAgreementBannersProps) {
  const { showAgreementScrolledErrorBanner, showConfirmationErrorBanner, worker, onDismiss } =
    props;

  const { howToContactSupportLink } = useSupportLinks();
  const { showErrorToast } = useToast();

  const handleContactSupport = async () => {
    try {
      await openInBrowser({ url: howToContactSupportLink });
    } catch (error) {
      logError(APP_V2_USER_EVENTS.OPEN_JOB_SEARCH_AGREEMENT_SUPPORT_ARTICLE_ERROR, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          errorMessage: toError(error).message,
        },
      });

      showErrorToast(
        `Error opening support article. Please manually go to the link: ${howToContactSupportLink}`
      );
      return;
    }

    logEvent(APP_V2_USER_EVENTS.OPENED_SUPPORT_ARTICLE, {
      articleLink: howToContactSupportLink,
      articleName: SupportArticles.HOW_TO_CONTACT_SUPPORT,
      userId: worker.userId,
      supportContext: SupportContext.JOB_SEARCH_AGREEMENT,
    });
  };

  function getSupportText(): string | undefined {
    if (showAgreementScrolledErrorBanner && showConfirmationErrorBanner) {
      return "You must scroll to the bottom of the agreement before submitting and click the checkbox to confirm and proceed. Having trouble?";
    }

    if (showAgreementScrolledErrorBanner) {
      return "You must scroll to the bottom of the agreement before submitting. Having trouble?";
    }

    if (showConfirmationErrorBanner) {
      return "You must click the checkbox to confirm and proceed. Having trouble?";
    }

    return undefined;
  }

  const supportText = getSupportText();

  if (isDefined(supportText)) {
    return (
      <SystemInlineBanner color="error" iconType="triangle-exclamation" sx={{ marginTop: 6 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Text variant="body2" color="inherit">
            {supportText}&nbsp;
            <Link
              variant="body2"
              sx={{
                color: "inherit",
                textDecorationColor: "inherit",
                ":hover": {
                  color: "inherit",
                  textDecoration: "underline",
                },
              }}
              onClick={handleContactSupport}
            >
              Contact our support team
            </Link>
            .
          </Text>
          {isDefined(onDismiss) && (
            <IconButton
              iconType="close"
              variant="ghost"
              size="small"
              aria-label="Dismiss"
              onClick={onDismiss}
            />
          )}
        </Box>
      </SystemInlineBanner>
    );
  }

  return null;
}
